/**
 * Downshift Scanner Utility Functions - Regime-Aware Version
 */

import React from 'react';
import { Typography, Tooltip } from '@mui/material';
import { SIGNAL_TAGS, TAG_COLORS } from './constants';

/**
 * Get sector name from result (backend provides vi/en)
 */
export const getSectorName = (result, language) => {
  if (result.sector_name) {
    // Backend provides sector_name with vi/en
    return result.sector_name[language] || result.sector_name.en || result.sector_code;
  }
  // Fallback to sector_code if sector_name not available
  return result.sector_code || '—';
};

/**
 * Translate technical tags to user-friendly text
 */
export const translateTag = (tag, t) => {
  // Distribution signals (HIGH regime)
  if (tag.includes(SIGNAL_TAGS.DIST)) {
    const count = tag.split('=')[1] || '';
    return t('downshift.tags.distribution', { count });
  }
  if (tag.includes(SIGNAL_TAGS.FAILED_BO)) return t('downshift.tags.failed_breakout');
  if (tag.includes(SIGNAL_TAGS.UPTHRUST)) return t('downshift.tags.upthrust');
  if (tag.includes(SIGNAL_TAGS.CHURN)) return t('downshift.tags.churning');
  
  // Volume signals
  if (tag.includes(SIGNAL_TAGS.DOWNVOL)) {
    const ratio = tag.split('=')[1];
    return ratio ? t('downshift.tags.volume_down_ratio', { ratio }) : t('downshift.tags.volume_down');
  }
  
  // Weekly signals
  if (tag.includes(SIGNAL_TAGS.W1_WEAK)) return t('downshift.tags.weekly_weak');
  if (tag.includes(SIGNAL_TAGS.W1_DIST)) return t('downshift.tags.weekly_distribution');
  if (tag.includes(SIGNAL_TAGS.W1_CHURN)) return t('downshift.tags.weekly_churning');
  
  // Sector signals
  if (tag.includes(SIGNAL_TAGS.SECTOR_RS_WEAK)) return t('downshift.tags.sector_weak');
  
  // Divergence signals
  if (tag.includes(SIGNAL_TAGS.OBV_DIV)) return t('downshift.tags.obv_divergence');
  if (tag.includes(SIGNAL_TAGS.AD_DIV)) return t('downshift.tags.ad_divergence');
  
  // Accumulation signals (LOW regime)
  if (tag.includes(SIGNAL_TAGS.CLIMAX_RECLAIM)) return t('downshift.tags.climax_reclaim');
  if (tag.includes(SIGNAL_TAGS.CLIMAX)) return t('downshift.tags.climax');
  if (tag.includes(SIGNAL_TAGS.ABSORPTION)) return t('downshift.tags.absorption');
  if (tag.includes(SIGNAL_TAGS.SPRING)) return t('downshift.tags.spring');
  if (tag.includes(SIGNAL_TAGS.ACCUM_VOL)) return t('downshift.tags.accumulation_volume');
  
  return tag; // Fallback to original
};

/**
 * Get tag color (muted, professional palette)
 */
export const getTagColor = (tag) => {
  // Accumulation signals - green
  if (tag.includes(SIGNAL_TAGS.CLIMAX) || 
      tag.includes(SIGNAL_TAGS.CLIMAX_RECLAIM) || 
      tag.includes(SIGNAL_TAGS.ABSORPTION) ||
      tag.includes(SIGNAL_TAGS.SPRING) ||
      tag.includes(SIGNAL_TAGS.ACCUM_VOL)) {
    return TAG_COLORS.SUCCESS;
  }
  
  // Critical signals - darker red
  if (tag.includes(SIGNAL_TAGS.DIST) || tag.includes(SIGNAL_TAGS.FAILED_BO)) {
    return TAG_COLORS.CRITICAL;
  }
  
  // Warning signals - muted orange
  if (tag.includes(SIGNAL_TAGS.UPTHRUST) || 
      tag.includes(SIGNAL_TAGS.DOWNVOL) || 
      tag.includes(SIGNAL_TAGS.W1_WEAK) ||
      tag.includes(SIGNAL_TAGS.CHURN)) {
    return TAG_COLORS.WARNING;
  }
  
  // Info signals - muted purple/teal
  if (tag.includes(SIGNAL_TAGS.SECTOR_RS_WEAK)) {
    return TAG_COLORS.INFO_PURPLE;
  }
  if (tag.includes(SIGNAL_TAGS.OBV_DIV) || tag.includes(SIGNAL_TAGS.AD_DIV)) {
    return TAG_COLORS.INFO_TEAL;
  }
  
  // Default - gray
  return TAG_COLORS.DEFAULT;
};

/**
 * Calculate volume change percentage
 */
export const calculateVolumeChange = (result) => {
  if (!result.avg_value_20 || result.avg_value_20 === 0) return 0;
  return ((result.volume * result.close - result.avg_value_20) / result.avg_value_20) * 100;
};

/**
 * Render volume change with color coding + tooltip
 * Compare today's trading value vs 20-day average
 */
export const renderVolumeChange = (volume, avgValue20, close, t) => {
  if (!avgValue20 || avgValue20 === 0) {
    return <Typography variant="body2">—</Typography>;
  }
  
  // Calculate today's value: volume * close
  const todayValue = volume * close;
  
  // Calculate % change vs 20-day average
  const change = ((todayValue - avgValue20) / avgValue20) * 100;
  
  // Calculate multiplier for tooltip
  const multiplier = todayValue / avgValue20;
  
  // Red for volume increase (distribution concern), green for decrease
  const color = change > 0 ? '#ef4444' : '#22c55e';
  const prefix = change > 0 ? '+' : '';
  
  // Tooltip message (localized)
  const avgValueText = `${(avgValue20 / 1e9).toFixed(1)}B`;
  const tooltipText = multiplier > 1 
    ? t('downshift.volume_change.tooltip_increase', { 
        multiplier: multiplier.toFixed(1), 
        avgValue: avgValueText 
      })
    : t('downshift.volume_change.tooltip_decrease', { 
        multiplier: (1/multiplier).toFixed(1), 
        avgValue: avgValueText 
      });
  
  return (
    <Tooltip title={tooltipText} arrow>
      <Typography variant="body2" fontWeight="bold" style={{ color, cursor: 'help' }}>
        {prefix}{change.toFixed(0)}%
      </Typography>
    </Tooltip>
  );
};

